import Table from 'components/Table';
import Timer from 'components/Timer';
import { useCallback } from 'react';
import CollectionStatusTag from './CollectionStatusTag';

import {
  formatNumberWithComma,
  getStakeholderTypeForCountry,
  renderInfoValue,
} from 'utils/helpers';
import testId from 'utils/testId';
import { sortByPriority } from '../helper';

import DateTime from 'utils/datetime';
import type { CollectionStatus, HotcashTableColumn } from '../types';

interface HotcashTableProps {
  country: string;
  data: HotcashTableColumn[];
  setData: React.Dispatch<React.SetStateAction<HotcashTableColumn[] | null>>;
  loading: boolean;
  pagination: {
    total: number;
    current: number;
    pageSize: number;
    onChange: (page: number, size: number) => void;
    onShowSizeChange: (page: number, size: number) => void;
  };
}

const HotcashTable = ({
  country,
  data,
  setData,
  loading,
  pagination,
}: HotcashTableProps) => {
  const handleTimerExpiry = useCallback(
    (paymentRecordId: string) => {
      setData((prevData) =>
        sortByPriority(
          prevData?.map((paymentRecord) =>
            paymentRecord?.id === paymentRecordId
              ? { ...paymentRecord, status: 'LATE' }
              : paymentRecord,
          ) as HotcashTableColumn[],
        ),
      );
    },
    [setData],
  );

  const renderTableHeader = useCallback(() => {
    return [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: CollectionStatus) => (
          <CollectionStatusTag status={status} />
        ),
        width: 120,
        align: 'center' as const,
      },
      {
        title: 'Time Left',
        dataIndex: 'timeLeft',
        render: (timeLeft: string, data: HotcashTableColumn) => {
          if (!timeLeft) return 'N/A';
          const allowedTimeToPay = DateTime.add(data?.startingTime, 1, 'days');
          const initialTime =
            data?.status === 'SETTLED'
              ? DateTime.calculateTimeRemaining(
                  data?.settlingTime!,
                  allowedTimeToPay,
                )
              : DateTime.calculateTimeRemaining(new Date(), timeLeft);
          return (
            <Timer
              enabled={data?.status !== 'SETTLED'}
              onTimerEnd={() => handleTimerExpiry(data?.id)}
              countBelowZero
              showDays={false}
              updateEvery={1000}
              toTime={new Date(timeLeft)}
              initialTime={initialTime}
              data-testid={
                testId.hotcashPage.table?.['text-hotcash-time_left_timer']
              }
            />
          );
        },
        key: 'timeLeft',
        width: 100,
      },
      {
        title: 'Agronomist',
        dataIndex: 'agronomistName',
        render: (agronomistName: string) => (
          <span
            data-testid={
              testId.hotcashPage.table?.['text-hotcash-agronomist_name']
            }
          >
            {renderInfoValue(agronomistName, 'N/A')}
          </span>
        ),
        key: 'agronomistName',
        width: 140,
        // align: 'center',
      },
      {
        title: 'Paid By',
        dataIndex: 'paidBy',
        render: (paidBy: string) => (
          <span
            data-testid={testId.hotcashPage.table?.['text-hotcash-paid_by']}
          >
            {renderInfoValue(paidBy, 'N/A')}
          </span>
        ),
        key: 'paidBy',
        width: 140,
      },
      {
        title: 'Cash held',
        dataIndex: 'cashHeld',
        render: (cashHeld: string, data: HotcashTableColumn) => (
          <span
            data-testid={testId.hotcashPage.table?.['text-hotcash-cash_held']}
          >
            {renderInfoValue(
              `${formatNumberWithComma(cashHeld)} ${data?.currency}`,
              'N/A',
            )}
          </span>
        ),
        key: 'cashHeld',
        width: 160,
      },
      {
        title: 'Farmer Name',
        dataIndex: 'farmerName',
        render: (farmerName: string) => (
          <span
            data-testid={testId.hotcashPage.table?.['text-hotcash-farmer_name']}
          >
            {renderInfoValue(farmerName, 'N/A')}
          </span>
        ),
        key: 'farmerName',
        width: 160,
      },
      {
        title: 'Invoice Number',
        dataIndex: 'invoiceNo',
        render: (invoiceNo: string) => (
          <span
            data-testid={
              testId.hotcashPage.table?.['text-hotcash-invoice_number']
            }
          >
            {renderInfoValue(invoiceNo, 'N/A')}
          </span>
        ),
        key: 'invoiceNo',
        width: 160,
      },
      {
        title: getStakeholderTypeForCountry(country, 'PB'),
        dataIndex: 'paddyBuyerName',
        key: 'paddyBuyerName',
        render: (paddyBuyerName: string) => (
          <span
            data-testid={
              testId.hotcashPage.table?.['text-hotcash-paddy_buyer_name']
            }
          >
            {renderInfoValue(paddyBuyerName, 'N/A')}
          </span>
        ),
        width: 160,
      },
      {
        title: 'Starting time',
        dataIndex: 'startingTime',
        key: 'startingTime',
        width: 160,

        render: (startingTime: string) => (
          <span
            data-testid={
              testId.hotcashPage.table?.['text-hotcash-starting_time']
            }
          >
            {startingTime
              ? renderInfoValue(
                  `${DateTime.format(startingTime, 'DD/MM/YYYY HH:mm:ss A')}`,
                )
              : 'N/A'}
          </span>
        ),
      },
    ];
  }, [handleTimerExpiry, country]);

  return (
    <Table
      loading={loading}
      pagination={pagination}
      tableColumns={data}
      tableHeader={renderTableHeader()}
      // to minimize cumulative layout shift
      style={{ minHeight: loading ? '30vh' : 0 }}
    />
  );
};

export default HotcashTable;
